import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { Outlet } from 'react-router-dom';
import { Loadable } from '@/components/common/PageLoading';
import Header from './Header';
import Sidebar from './Sidebar';
import AppBreadcrumbs from './AppBreadcrumbs';
import { useNotificationsLive } from '@/hooks/useNotificationsLive';
import { useModuleSync } from '@/core/modules/useModuleSync';
import { usePropertyNavFeatures } from '@/modules/property/navFeatures';
import { useProfileSync } from '@/auth/useProfileSync';
import { useOrgLocaleSync } from '@/api/locale';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { lazy, Suspense, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import LockScreen from '@/components/account/LockScreen';
import { useAuthStore } from '@/auth/authStore';
import { Perm } from '@/auth/permissions';
import { Modules } from '@/core/modules/moduleKeys';
import { useModuleStore } from '@/core/modules/moduleStore';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';
import { registerThisDevice } from '@/offline/device';

/**
 * Application shell: fixed header, collapsible sidebar, breadcrumb trail and the
 * routed page content. Fully responsive — the sidebar becomes a temporary
 * overlay on small screens.
 *
 * The persistent sidebar reserves its own width in the flex row (its root
 * collapses to 0 when closed), so the <main> region simply fills the remaining
 * space with `flexGrow`. It must NOT also add a left margin, or the content
 * would be offset twice and drift to the right.
 */
const ReportIncident = lazy(() => import('@/modules/hospital/components/ReportIncident'));

export default function AppLayout() {
  useNotificationsLive();
  useModuleSync();
  usePropertyNavFeatures();
  useProfileSync();
  useOrgLocaleSync();
  useIdleTimeout();
  const { online, pending, lastReplay } = useOfflineStatus();
  const healthOn = useModuleStore((s) => s.enabled[Modules.Health] === true);
  const canReport = useAuthStore((s) => s.hasPermission(Perm.Health.Use)) && healthOn;
  // The installed app registers its device at every start (ToR 4.3.1): a wipe asked for meanwhile is carried out here.
  useEffect(() => { void registerThisDevice(); }, []);
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            p: { xs: 2, md: 3 },
          }}
        >
          <AppBreadcrumbs />
          {!online && <Alert severity="warning" sx={{ mb: 1.5 }}>You are offline. Packs kept on this device can still be read; {pending > 0 ? `${pending} change${pending === 1 ? '' : 's'} will be sent when the network returns.` : 'anything you note is sent when the network returns.'}</Alert>}
          {online && lastReplay && lastReplay.sent > 0 && <Alert severity="success" sx={{ mb: 1.5 }}>{lastReplay.sent} change{lastReplay.sent === 1 ? '' : 's'} made offline {lastReplay.sent === 1 ? 'has' : 'have'} been sent.</Alert>}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Loadable><Outlet /></Loadable>
          </Box>
          <LockScreen />
          {canReport && <Suspense fallback={null}><ReportIncident /></Suspense>}
        </Box>
      </Box>
    </Box>
  );
}
